import { useEffect, useState } from 'react';

import {
  MedicalImageRender,
  type FullHeadMPRSession,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import {
  readFullHeadMprImageOptionsSync,
  writeFullHeadMprImageOptionsSync,
  type MPRAnnotationControls,
  type MPRAnnotationTool,
  type MPRImageControls,
  type MPRImageTool,
  type MPRInputMode,
  type MPRTool,
} from '@/features/MPR';

import { FULL_HEAD_MPR_INPUT_MODE_BY_MODE } from '../config';

type UseFullHeadMPRToolsParams = {
  isReady: boolean;
  canEditAnnotations?: boolean;
  canEditImageOptions?: boolean;
  session?: FullHeadMPRSession | null;
  onReset: () => void;
};

type UseFullHeadMPRToolsResult = {
  activeMode: MPRInputMode;
  annotations: MPRAnnotationControls;
  imageOptions: MPRImageControls;
  isAxesVisible: boolean;
  isImageOptionsSynced: boolean;
  isZoomSynced: boolean;
  handleToolClick: (tool: MPRTool) => void;
  setAxesVisible: (isVisible: boolean) => void;
  setImageOptionsSynced: (isSynced: boolean) => void;
  setZoomSynced: (isSynced: boolean) => void;
};

export const useFullHeadMPRTools = (
  params: UseFullHeadMPRToolsParams,
): UseFullHeadMPRToolsResult => {
  const {
    isReady,
    canEditAnnotations = false,
    canEditImageOptions = false,
    onReset,
    session,
  } = params;

  const [activeMode, setMode] = useState<MPRInputMode>('mainMode');
  const [isAxesVisible, setAxesVisible] = useState(true);
  const [isZoomSynced, setZoomSynced] = useState(false);
  const [isImageOptionsSynced, setImageOptionsSyncedState] = useState(
    readFullHeadMprImageOptionsSync,
  );
  const [activeAnnotationTool, setAnnotationTool] =
    useState<MPRAnnotationTool | null>(null);
  const [hasAnnotationSelection, setAnnotationSelection] = useState(false);
  const [isAnnotationsVisible, setAnnotationsVisible] = useState(true);
  const [activeImageTool, setImageTool] = useState<MPRImageTool | null>(null);

  useEffect(() => {
    if (!isReady) {
      setAnnotationSelection(false);
      return;
    }

    const onSelectionChanged = (event: { selected: boolean }) =>
      setAnnotationSelection(event.selected);
    const removeListener = session
      ? session.addEventListener(
          'fullHeadMPRAnnotationSelectionChanged',
          onSelectionChanged,
        )
      : MedicalImageRender.addEventListener(
          'fullHeadMPRAnnotationSelectionChanged',
          onSelectionChanged,
          'fullHeadMPR',
        );

    return () => {
      removeListener?.();
    };
  }, [isReady, session]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (session) {
      session.setImageOptionsSyncEnabled(isImageOptionsSynced);
    } else {
      MedicalImageRender.setFullHeadMPRImageOptionsSyncEnabled(
        isImageOptionsSynced,
      );
    }
  }, [isReady, isImageOptionsSynced, session]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const tool = canEditImageOptions ? activeImageTool : null;
    if (session) {
      session.setImageTool(tool);
    } else {
      MedicalImageRender.setFullHeadMPRImageTool(tool);
    }
  }, [isReady, session, activeImageTool, canEditImageOptions]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const tool = canEditAnnotations ? activeAnnotationTool : null;
    if (session) {
      session.setAnnotationTool(tool);
    } else {
      MedicalImageRender.setFullHeadMPRAnnotationTool(tool);
    }
  }, [isReady, session, activeAnnotationTool, canEditAnnotations]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (session) {
      session.setAnnotationsVisible(isAnnotationsVisible);
    } else {
      MedicalImageRender.setFullHeadMPRAnnotationsVisible(isAnnotationsVisible);
    }
  }, [isReady, session, isAnnotationsVisible]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const mode = FULL_HEAD_MPR_INPUT_MODE_BY_MODE[activeMode];
    if (session) {
      session.setInputMode(mode);
    } else {
      MedicalImageRender.setFullHeadMPRInputMode(mode);
    }
  }, [isReady, activeMode, session]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (session) {
      session.setCrosshairsVisible(isAxesVisible);
    } else {
      MedicalImageRender.setFullHeadMPRCrosshairsVisible(isAxesVisible);
    }
  }, [isReady, isAxesVisible, session]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (session) {
      session.setZoomSyncEnabled(isZoomSynced);
    } else {
      MedicalImageRender.setFullHeadMPRZoomSyncEnabled(isZoomSynced);
    }
  }, [isReady, isZoomSynced, session]);

  const setImageOptionsSynced = (isSynced: boolean) => {
    writeFullHeadMprImageOptionsSync(isSynced);
    setImageOptionsSyncedState(isSynced);
  };

  const handleImageToolClick = (tool: MPRImageTool) => {
    if (!isReady || !canEditImageOptions) {
      return;
    }

    setAnnotationTool(null);
    setImageTool(activeImageTool === tool ? null : tool);
  };

  const handleToolClick = (tool: MPRTool) => {
    if (!isReady) {
      return;
    }

    if (tool === 'contrastBrightnessMode') {
      handleImageToolClick('brightnessContrast');
      return;
    }

    setAnnotationTool(null);
    setImageTool(null);
    if (tool === 'reset') {
      onReset();
      setMode('mainMode');
      return;
    }

    setMode(tool);
  };

  return {
    activeMode,
    imageOptions: {
      activeTool: canEditImageOptions ? activeImageTool : null,
      canEdit: isReady && canEditImageOptions,
      onToolClick: handleImageToolClick,
    },
    annotations: {
      activeTool: canEditAnnotations ? activeAnnotationTool : null,
      canEdit: isReady && canEditAnnotations,
      hasSelection: isReady && canEditAnnotations && hasAnnotationSelection,
      isVisible: isAnnotationsVisible,
      onToolClick: (tool) => {
        if (!isReady || !canEditAnnotations) {
          return;
        }

        setAnnotationTool(activeAnnotationTool === tool ? null : tool);
        setImageTool(null);
        setAnnotationsVisible(true);
      },
      onVisibilityChange: setAnnotationsVisible,
      onCancel: () => {
        if (!isReady) {
          return false;
        }

        const handled = session
          ? session.cancelAnnotationInteraction()
          : MedicalImageRender.cancelFullHeadMPRAnnotationInteraction();
        if (activeAnnotationTool) {
          setAnnotationTool(null);
        }
        if (activeImageTool) {
          setImageTool(null);
        }
        setAnnotationSelection(false);
        return (
          handled || activeAnnotationTool !== null || activeImageTool !== null
        );
      },
      onDeleteSelected: () => {
        if (!isReady || !canEditAnnotations) {
          return false;
        }

        const deleted =
          (session
            ? session.deleteSelectedAnnotation()
            : MedicalImageRender.deleteFullHeadMPRSelectedAnnotation()) ===
          true;
        if (deleted) {
          setAnnotationSelection(false);
        }
        return deleted;
      },
      onFinish: () => {
        if (!isReady || !canEditAnnotations) {
          return false;
        }

        return (
          (session
            ? session.finishAnnotationInteraction()
            : MedicalImageRender.finishFullHeadMPRAnnotationInteraction()) ===
          true
        );
      },
    },
    isAxesVisible,
    isImageOptionsSynced,
    isZoomSynced,
    handleToolClick,
    setAxesVisible,
    setImageOptionsSynced,
    setZoomSynced,
  };
};
